import { useLayoutEffect, useRef } from 'react'

const BOTTOM_GAP_PX = 32
const MIN_HEIGHT_PX = 320

const CCQ_GRID_ATTRIBUTE = 'data-ccq-grid'

// Sums every ancestor's scroll offset instead of guessing the real scroller from CSS: some ancestors have
// scrollable overflow but just grow with their content.
const scrolledBy = (element: HTMLElement): number => {
  const parent = element.parentElement
  return parent === null ? 0 : parent.scrollTop + scrolledBy(parent)
}

// A sticky element (the panel in the rail) moves with the scroll, so it's measured from the grid's top instead.
export const CcqFillAnchors = { GRID: 'grid', SELF: 'self' } as const

export type CcqFillAnchor = (typeof CcqFillAnchors)[keyof typeof CcqFillAnchors]

// Measured as if the page were scrolled to the top, so it only changes on resize: a height that followed
// the scroll position would make the page taller as it scrolls.
export const useFillToViewportBottom = <T extends HTMLElement>(isActive: boolean, anchor: CcqFillAnchor) => {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    const element = ref.current
    const grid = element?.closest<HTMLElement>(`[${CCQ_GRID_ATTRIBUTE}]`)
    const page = grid?.parentElement
    if (!isActive || !element || !grid || !page) {
      return
    }
    const measured = anchor === CcqFillAnchors.GRID ? grid : element
    let frame = 0
    const fit = () => {
      const topWhenScrolledUp = measured.getBoundingClientRect().top + scrolledBy(measured)
      // Leave at least the page's bottom padding, or the element would push the page past the viewport.
      const belowGrid = Number.parseFloat(getComputedStyle(page).paddingBottom) || 0
      const bottomGap = Math.max(BOTTOM_GAP_PX, belowGrid)
      element.style.height = `${Math.max(window.innerHeight - topWhenScrolledUp - bottomGap, MIN_HEIGHT_PX)}px`
    }
    const scheduleFit = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(fit)
    }
    fit()
    window.addEventListener('resize', scheduleFit)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', scheduleFit)
      element.style.height = ''
    }
  }, [isActive, anchor])

  return ref
}
